/* eslint-disable react/display-name */
import { mq, styled } from '@creditas/stylitas'
import { styled as _styled } from '@creditas-ui/system'
import { media, getTypography } from '@creditas-ui/utilities'
import { ButtonCta } from 'components/ButtonCta'
import { Typography } from 'components/Typography'
import { Link } from 'gatsby'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { fillMediaBase } from 'ui/utils/fillMediaBase'
import { LazyImage } from 'components/LazyImage'
import { getColorToken } from 'src/utils/getColorToken'

const Wrapper = styled.div`
  width: 100%;
  padding: 16px;
  margin: 0;
  clear: both;
  display: block;

  ${media.up('5xl')} {
    width: 83.33333333%;
    margin: 48px auto;
    padding: 0;
  }

  ${mq({
    marginTop: fillMediaBase('40px', '16px'),
  })}
`

const Category = styled(Link)`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 1.5px;
  text-decoration: none;
  text-transform: uppercase;
  color: ${getColorToken('primary.60')};
`

const Title = _styled(Typography)`

  color: ${getColorToken('neutral.90')};

  ${getTypography('headingLgLight')}

  line-height: 48px;
  margin-top: 12px;

  ${media.up('5xl')} {
    ${getTypography('heading5xlLight')};
    line-height: 90px;
    margin-top: 0;
  }
`

const Subtitle = _styled(Typography)`
  color: ${getColorToken('neutral.60')};
  margin-top: 16px;
  padding-bottom: 40px;
  border-bottom: 1px solid ${getColorToken('neutral.15')};

  ${getTypography('bodyMdRegular')}

  ${media.up('5xl')} {
    ${getTypography('bodyXlRegular')}
    line-height: 32px;
  }
`

const Image = styled(LazyImage)`
  width: 100%;
  max-width: 600px;
  margin: 24px auto;

  ${media.up('5xl')} {
    margin: 48px auto;
  }
`

const Body = styled.div`
  width: 100%;
  margin: auto;

  ${mq({
    maxWidth: fillMediaBase('100%', '805px'),
  })}
`

const Head = styled.div`
  width: 100%;
  margin: auto;

  ${mq({
    maxWidth: fillMediaBase('100%', '1016px'),
  })}
`

const Bold = styled.b`
  font-weight: 700;
`

const Italic = styled.b`
  font-style: italic;
`

const Text = _styled(Typography)`
  
  ${getTypography('bodyLgRegular')}
  color: ${getColorToken('neutral.60')};
  line-height: 28px;

  ${media.up('5xl')} {
    ${getTypography('bodyXlRegular')}
    line-height: 32px;
  }
`

const AssetContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
  }
`

const PostTextSubtitle = styled.h2`
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0.5px;
  color: ${getColorToken('neutral.90')};
  margin: 32px 0 24px;
`

const PostTextTitle = styled.h2`
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 40px;
  letter-spacing: 0.5px;
  color: ${getColorToken('neutral.90')};
`

const OrderedList = styled.ol`
  padding-left: 40px;

  li .contentWrapper {
    padding: 0 0 0 8px;
  }

  li::marker {
    color: ${getColorToken('neutral.60')};
    font-weight: 700;
  }

  font-size: 20px;
`

const UnorderedList = styled.ul`
  padding-left: 40px;

  li .contentWrapper {
    padding: 0 0 0 8px;
  }

  li::marker {
    color: ${getColorToken('neutral.60')};
    font-weight: 700;
  }

  font-size: 20px;
`

const TextContentWrapper = styled.div`
  margin-right: auto;
  margin-left: auto;
  margin-top: 24px;
`

const TextLink = styled.a`
  text-decoration: none;
  color: ${getColorToken('primary.60')};
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 32px;

  letter-spacing: 0.5px;
`

const ButtonWrapper = styled.li`
  width: 100%;
  margin: 24px 16px 48px;
  clear: both;
  display: block;
  padding: 0;

  ${media.up('5xl')} {
    margin: 24px auto 48px;
    width: 83.33333333%;
  }
`

const Button = styled(ButtonCta)`
  border: 1px solid transparent;
  text-decoration: none;
  color: ${getColorToken('primary.60')};
  clear: both;
  display: flex;
  width: 100%;
  padding: 0;
  margin: auto;
  max-width: 805px;

  ${media.up('5xl')} {
    width: 83.33333333%;
  }

  &:hover {
    color: ${getColorToken('primary.60')};
    text-decoration: underline;
  }

  svg {
    color: ${getColorToken('primary.60')};
    display: block;
    margin-left: 8px;
  }
`

const htmlOptions = {
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
    [MARKS.ITALIC]: text => <Italic>{text}</Italic>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <TextContentWrapper>
        <Text>{children}</Text>
      </TextContentWrapper>
    ),
    [BLOCKS.HEADING_1]: (node, children) => (
      <TextContentWrapper>
        <PostTextTitle>{children}</PostTextTitle>
      </TextContentWrapper>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <TextContentWrapper>
        <OrderedList>{children}</OrderedList>
      </TextContentWrapper>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <TextContentWrapper>
        <UnorderedList>{children}</UnorderedList>
      </TextContentWrapper>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <TextContentWrapper>
        <PostTextSubtitle>{children}</PostTextSubtitle>
      </TextContentWrapper>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <TextContentWrapper>
        <PostTextSubtitle as="h3">{children}</PostTextSubtitle>
      </TextContentWrapper>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <TextContentWrapper>
        <PostTextSubtitle as="h4">{children}</PostTextSubtitle>
      </TextContentWrapper>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <TextContentWrapper>
        <PostTextSubtitle as="h5">{children}</PostTextSubtitle>
      </TextContentWrapper>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <TextContentWrapper>
        <PostTextSubtitle as="h6">{children}</PostTextSubtitle>
      </TextContentWrapper>
    ),
    [BLOCKS.EMBEDDED_ASSET]: node => (
      <AssetContainer>
        <img
          alt={node.data.target.fields.file?.['en-US']?.description}
          src={`${node.data.target.fields.file?.['en-US']?.url}`}
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
      </AssetContainer>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <TextLink href={node.data.uri}>{children}</TextLink>
    ),
  },
}

export {
  Wrapper,
  Category,
  Head,
  Title,
  Subtitle,
  Body,
  Image,
  ButtonWrapper,
  Button,
  htmlOptions,
}
